import React, { useCallback, useState } from 'react';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ErrorIcon from '@mui/icons-material/Error';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import CustomButton from 'components/button/CustomButton';
import MemberSelect from 'components/form/MemberSelect';
import {
  ContractRequestDetailResult,
  CreateContractRequestParams,
  TemplateItemInput,
  createContractRequest,
} from 'stores/ContractRequestStoreAdapter';
import { MemberListResult } from 'stores/MemberStoreAdapter';

type DuplicateRowStatus = 'pending' | 'processing' | 'success' | 'error';

type DuplicateRow = {
  memberId: string;
  memberName: string;
  partnerName: string | null;
  status: DuplicateRowStatus;
  errorMessage?: string;
};

type Phase = 'select' | 'preview' | 'registering' | 'done';

type ContractRequestDuplicateDialogProps = {
  /** ダイアログ開閉状態 */
  open: boolean;
  /** ダイアログを閉じるハンドラ */
  onClose: () => void;
  /** 複製元の契約申請詳細 */
  sourceDetail: ContractRequestDetailResult;
  /** 要員一覧 */
  members: MemberListResult[];
  /** 完了後のコールバック */
  onComplete?: () => void;
};

/**
 * 契約申請 複製ダイアログ
 * メンバー選択 → プレビュー確認 → 1件ずつ登録 のフローを管理する
 */
const ContractRequestDuplicateDialog: React.FC<ContractRequestDuplicateDialogProps> = ({
  open,
  onClose,
  sourceDetail,
  members,
  onComplete,
}) => {
  const [phase, setPhase] = useState<Phase>('select');
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [rows, setRows] = useState<DuplicateRow[]>([]);

  const hasRegisterErrors = rows.some((r) => r.status === 'error');
  const allRegisterSuccess = rows.length > 0 && rows.every((r) => r.status === 'success');

  const resetState = useCallback(() => {
    setPhase('select');
    setSelectedMemberIds([]);
    setRows([]);
  }, []);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleConfirm = () => {
    // メンバー選択 → プレビューへ
    const newRows: DuplicateRow[] = selectedMemberIds.map((memberId) => {
      const member = members.find((m) => m.memberId === memberId);
      return {
        memberId,
        memberName: member?.memberName || '',
        partnerName: member?.partnerName || null,
        status: 'pending',
      };
    });
    setRows(newRows);
    setPhase('preview');
  };

  const handleBack = () => {
    // プレビュー → メンバー選択に戻る
    setRows([]);
    setPhase('select');
  };

  /** 複製元の情報からCreateContractRequestParamsを構築する */
  const buildDuplicateParams = (memberId: string): CreateContractRequestParams => {
    // テンプレート項目の変換
    const templateItems: TemplateItemInput[] = Object.entries(sourceDetail.templateItems || {}).map(
      ([itemKey, dto]) => ({
        itemKey,
        values: dto.mainValues || [],
      }),
    );

    return {
      requestStatus: 'ADJUSTING',
      personInChargeId: sourceDetail.personInChargeId,
      projectId: sourceDetail.projectId,
      managerId: sourceDetail.managerId,
      memberId,
      prefectureId: sourceDetail.prefectureId?.main || undefined,
      location: sourceDetail.location?.main || undefined,
      contractTypeId: sourceDetail.contractTypeId?.main || undefined,
      contractStartDate: sourceDetail.contractStartDate?.main || undefined,
      contractEndDate: sourceDetail.contractEndDate?.main || undefined,
      costAmount: sourceDetail.costAmount?.main ?? undefined,
      salesAmount: sourceDetail.salesAmount?.main ?? undefined,
      operationRate: sourceDetail.operationRate?.main ?? undefined,
      contractUnitId: sourceDetail.contractUnitId?.main || undefined,
      adjustmentUpperLimit: sourceDetail.adjustmentUpperLimit?.main ?? undefined,
      adjustmentLowerLimit: sourceDetail.adjustmentLowerLimit?.main ?? undefined,
      needsOriginSheet: sourceDetail.needsOriginSheet?.main ?? undefined,
      originSheetDestination: sourceDetail.originSheetDestination?.main || undefined,
      remarks: sourceDetail.remarks || undefined,
      templateItems: templateItems.length > 0 ? templateItems : undefined,
    };
  };

  const handleExecute = async () => {
    setPhase('registering');

    const results = [...rows];
    for (let i = 0; i < results.length; i++) {
      results[i] = { ...results[i], status: 'processing' };
      setRows([...results]);

      try {
        const params = buildDuplicateParams(results[i].memberId);
        await createContractRequest(params);
        results[i] = { ...results[i], status: 'success' };
      } catch (e: unknown) {
        const errorMsg = e instanceof Error ? e.message : '登録に失敗しました';
        results[i] = { ...results[i], status: 'error', errorMessage: errorMsg };
      }
      setRows([...results]);
    }

    setPhase('done');
  };

  const handleRetry = async () => {
    setPhase('registering');

    const results = [...rows];
    for (let i = 0; i < results.length; i++) {
      if (results[i].status !== 'error') continue;

      results[i] = { ...results[i], status: 'processing', errorMessage: undefined };
      setRows([...results]);

      try {
        const params = buildDuplicateParams(results[i].memberId);
        await createContractRequest(params);
        results[i] = { ...results[i], status: 'success' };
      } catch (e: unknown) {
        const errorMsg = e instanceof Error ? e.message : '登録に失敗しました';
        results[i] = { ...results[i], status: 'error', errorMessage: errorMsg };
      }
      setRows([...results]);
    }

    setPhase('done');
  };

  const handleDone = () => {
    resetState();
    onClose();
    onComplete?.();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={phase === 'registering' ? undefined : handleClose}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          border: 1,
          borderColor: 'divider',
          minHeight: phase === 'select' ? undefined : '50vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          契約申請を複製する
        </Typography>
        {phase !== 'registering' && (
          <IconButton size="small" onClick={handleClose} sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Stack spacing={2}>
            {/* メンバー選択フェーズ */}
            {phase === 'select' && (
              <>
                <Typography variant="body2" color="text.secondary">
                  複製先のメンバーを選択してください。選択したメンバーごとに申請が複製されます。
                </Typography>
                <Box>
                  <MemberSelect
                    mode="multi"
                    members={members.filter((m) => !m.isDeleted)}
                    selectedMemberIds={selectedMemberIds}
                    onChange={setSelectedMemberIds}
                    label="メンバー選択"
                  />
                </Box>
                {selectedMemberIds.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedMemberIds.length}名のメンバーが選択されています
                  </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <CustomButton
                    label="確認"
                    buttonVariant="primary"
                    icon={<ContentCopyIcon />}
                    onClick={handleConfirm}
                    disabled={selectedMemberIds.length === 0}
                  />
                </Box>
              </>
            )}

            {/* プレビューフェーズ */}
            {(phase === 'preview' || phase === 'registering' || phase === 'done') && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {phase === 'preview'
                    ? '以下のメンバーの新規申請を作成しますか？'
                    : phase === 'registering'
                      ? '登録処理中...'
                      : allRegisterSuccess
                        ? '全て登録が完了しました。'
                        : '一部の登録に失敗しました。'}
                </Typography>

                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    maxHeight: '40vh',
                    overflow: 'auto',
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: 40,
                            minWidth: 40,
                          }}
                        />
                        <TableCell sx={{ fontWeight: 600 }}>メンバー名</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>パートナー</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow key={row.memberId}>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <StatusIcon status={row.status} error={row.errorMessage} />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{row.memberName}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{row.partnerName || '社員'}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* アクションボタン */}
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  {phase === 'preview' && (
                    <>
                      <CustomButton
                        label="キャンセル"
                        buttonVariant="secondary"
                        onClick={handleBack}
                      />
                      <CustomButton
                        label="実行"
                        buttonVariant="primary"
                        icon={<PlayArrowIcon />}
                        onClick={handleExecute}
                      />
                    </>
                  )}
                  {phase === 'done' && !hasRegisterErrors && (
                    <CustomButton
                      label="完了"
                      buttonVariant="primary"
                      icon={<DoneAllIcon />}
                      onClick={handleDone}
                    />
                  )}
                  {phase === 'done' && hasRegisterErrors && (
                    <>
                      <CustomButton label="閉じる" buttonVariant="secondary" onClick={handleDone} />
                      <CustomButton
                        label="再実行"
                        buttonVariant="primary"
                        icon={<ReplayIcon />}
                        onClick={handleRetry}
                      />
                    </>
                  )}
                </Stack>
              </>
            )}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

/** ステータスアイコン */
const StatusIcon: React.FC<{ status: DuplicateRowStatus; error?: string }> = ({
  status,
  error,
}) => {
  switch (status) {
    case 'pending':
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      );
    case 'processing':
      return <CircularProgress size={18} />;
    case 'success':
      return (
        <Tooltip title="登録成功">
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
        </Tooltip>
      );
    case 'error':
      return (
        <Tooltip title={error || '登録失敗'}>
          <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />
        </Tooltip>
      );
  }
};

export default ContractRequestDuplicateDialog;
